import { useEffect, useMemo } from 'react'
import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import { Switch } from 'components/Switch'
import { Typography } from 'components/Typography'
import StraightRebateCondition from 'features/ContractDetails/RebateConditions/RebateConditionsForm/StraightRebateCondition/StraightRebateCondition'
import GrowthRebateCondition from 'features/ContractDetails/RebateConditions/RebateConditionsForm/GrowthRebateCondition/GrowthRebateCondition'
import RebateConditionsFormProvider from 'features/ContractDetails/RebateConditions/RebateConditionsForm/Providers/RebateConditionsFormProvider'
import MarketShareRebateCondition from './MarketShareRebateCondition/MarketShareRebateCondition'
import { Popup } from '../../../../components/Popup'
import { Select } from 'components/Select'
import { Banner } from '../../../../components/Banner/Banner'
import usePopup from '../../../../hooks/usePopup'
import useRebateConditionsForm from 'features/ContractDetails/RebateConditions/RebateConditionsForm/hooks/useRebateConditionsForm'
import { useShowPopup } from '../../../../hooks/useShowPopup'
import { useContractDetailsContext } from '../../Providers/ContractDetailsContextProvider'
import { BTN_TXT, LABELS, PLACEHOLDERS } from 'constants/txt'
import {
  REBATE_CONDITIONS_TYPE,
  REBATE_CONDITIONS_TYPE_OPTIONS
} from 'features/ContractDetails/RebateConditions/RebateConditionsForm/constants'
import { IRebateConditionForm } from 'features/ContractDetails/RebateConditions/RebateConditionsForm/types'
import './styles.scss'
import { RowLayout } from 'features/MyCommunity/Vendors/VendorCreation/General/VendorForm/RowLayout'
import { FloatFooter } from 'components/FloatFooter/FloatFooter'
import { ViewNoteButton } from 'features/ContractDetails/ViewNote/ViewNoteButton'
import { ViewNotePopup } from 'features/ContractDetails/ViewNote/ViewNotePopup'

const RebateConditionsForm = () => {
  const rebateConditionsForm = useRebateConditionsForm()

  const contractDetailsContext = useContractDetailsContext()
  const { details } = contractDetailsContext.state
  const { setActiveTab, updateRebateNoteAsync } = contractDetailsContext.actions
  const discardChangesPopup = usePopup()

  const {
    form,
    type,
    hasFormChanges,
    handleFinishForm,
    handleCancelForm,
    handleChangeForm,
    isChanged,
    applyToHospitals,
    setIsChanged
  } = rebateConditionsForm

  const [showPopup, confirmNavigation, cancelNavigation] =
    useShowPopup(isChanged)

  const canViewNote = !!details.can_view_rebate_note
  const canEditNote = !!details.can_manage_rebate_note
  const note = details.rebate_note ?? null

  const cancelDiscardChanges = () => {
    discardChangesPopup.actions.close()
  }

  useEffect(() => {
    if (!showPopup) {
      setActiveTab('rebate_conditions')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopup])

  const RebateConditionType = useMemo(
    () => (
      <Select
        options={REBATE_CONDITIONS_TYPE_OPTIONS}
        propsItem={{
          label: LABELS.REBATE_TYPE,
          name: 'rebate_type',
          rules: [
            {
              required: true
            }
          ]
        }}
        propsSelect={{
          placeholder: PLACEHOLDERS.PLEASE_SELECT,
          onChange: handleChangeForm
        }}
      />
    ),
    [handleChangeForm]
  )

  const rebateCondition = useMemo(() => {
    switch (type) {
      case REBATE_CONDITIONS_TYPE.STRAIGHT_REBATE:
        return <StraightRebateCondition handleChangeForm={handleChangeForm} />
      case REBATE_CONDITIONS_TYPE.GROWTH_REBATE:
        return <GrowthRebateCondition handleChangeForm={handleChangeForm} />
      case REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE:
        return (
          <MarketShareRebateCondition
            applyToHospitals={applyToHospitals}
            handleChangeForm={handleChangeForm}
            form={form}
          />
        )
      default:
        return null
    }
  }, [type, handleChangeForm, applyToHospitals, form])

  const rebateDescription = useMemo(() => {
    let text = ''
    switch (type) {
      case REBATE_CONDITIONS_TYPE.STRAIGHT_REBATE:
        text =
          'A fixed rebate applied uniformly to all purchases, regardless of volume or performance.'
        break
      case REBATE_CONDITIONS_TYPE.GROWTH_REBATE:
        text =
          'Rebate based on increasing purchase volume over a defined baseline or prior period.'
        break
      case REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE:
        text =
          "Rebated based on quarterly assessment of Vendor's Marketshare. Each quarter actual % must be greater than or equal to the threshold %, to be eligible for a rebate payment"
        break
      default:
        return null
    }
    return (
      <Banner.Info text={text} className="rebate-conditions-form__banner" />
    )
  }, [type])

  const rebateNote = useMemo(() => {
    return (
      <div className="mt-16">
        <ViewNoteButton
          canViewNote={canViewNote}
          canEditNote={canEditNote}
          note={note}
          title="Rebate note"
        />
        <ViewNotePopup
          note={note}
          updateContractNote={updateRebateNoteAsync}
          title="Rebate note"
          subtitle="Notes about this rebate visible only for users from your Health System."
        />
      </div>
    )
  }, [canEditNote, canViewNote, note, updateRebateNoteAsync])

  const TieredSection = useMemo(() => {
    if (type !== REBATE_CONDITIONS_TYPE.MARKET_SHARE_REBATE) return null

    return (
      <div className="rebate-conditions-form__tiered row gap-12 align-center">
        <Form.Item name="is_tiered" valuePropName="checked" noStyle>
          <Switch withLabels onChange={handleChangeForm} />
        </Form.Item>
        <div className="column">
          <Typography.Body1>Tiered</Typography.Body1>
          <Typography.Label className="grey-text">
            A single threshold and a single rebate %
          </Typography.Label>
        </div>
      </div>
    )
  }, [handleChangeForm, type])

  const submitCancellation = () => {
    handleCancelForm()
    discardChangesPopup.actions.close()
    setIsChanged(false)
  }

  return (
    <RebateConditionsFormProvider {...rebateConditionsForm}>
      <CustomForm<IRebateConditionForm>
        id="rebate-conditions-form-id"
        className="rebate-conditions-form column gap-24"
        form={form}
        onChange={handleChangeForm}
        onFinish={handleFinishForm}
        onFieldsChange={() => setIsChanged(true)}
      >
        <RowLayout title="Rebate type">
          <div className="column gap-4">
            {RebateConditionType}
            {TieredSection}
            {rebateDescription}
            {rebateNote}
          </div>
        </RowLayout>
        {rebateCondition}
      </CustomForm>
      <Popup.Discard
        visible={showPopup ? showPopup : discardChangesPopup.state.visible}
        onCancel={showPopup ? cancelNavigation : cancelDiscardChanges}
        onSubmit={showPopup ? confirmNavigation : submitCancellation}
      />
      <FloatFooter
        isActive={hasFormChanges}
        onCancel={handleCancelForm}
        onSave={form.submit}
        saveBtnText={BTN_TXT.SAVE}
      />
    </RebateConditionsFormProvider>
  )
}

export default RebateConditionsForm
